import React from 'react';
import PropTypes from 'prop-types';
import { Field } from 'redux-form';
import { schedulePropTypes } from 'constants/propTypes';
import { Form, Button } from 'components/Form';
import Message from 'components/Message';
import { TooltipToggle, TooltipContent } from 'components/Tooltip';
import { parseNumber } from 'helpers/core';
import { classNameWithModifiers as withMods } from 'helpers/class-name';

import { reservationsOverviewMessages as messages } from './ScheduleDetail.messages';
import { NetworkReservationsDropdown, NetworkReservationsStepper } from '../Components';
import './ReservationsOverview.scss';

function ReservationsOverview(props) {
  const {
    schedule,
    displaySpotManagement,
    handleSSAddSpot,
    isDesktop,
    currentValues = {},
    incrementClasspassSpots,
    decrementClasspassSpots,
    actOnOpportunity,
  } = props;

  const {
    classpassAlmostSoldOut,
    classpassSoldOut,
    classpassSpotsUnfilled,
    hasIntegrationData,
    isFull,
    isInThePast,
    isInProgress,
    isLocked,
    opportunity = {},
    reservationsStopped,
    smartspotEnabled,
    spots = {},
  } = schedule;
  const { classpassBooked, classpassDemand, classpassTotal, maxCapacity, totalBooked } = spots;
  const { recommendation } = opportunity;

  const { classpassSpots: currentClasspassSpots } = currentValues;
  const showClasspassSpotsPicker =
    displaySpotManagement && !isInThePast && !isInProgress && !smartspotEnabled && !reservationsStopped;
  const showBookingsPausedTooltip = reservationsStopped;
  const showSSAddSpotButton =
    displaySpotManagement &&
    !isFull &&
    !isInThePast &&
    !isLocked &&
    (classpassSpotsUnfilled === 0 || classpassSpotsUnfilled === Infinity) &&
    smartspotEnabled;
  const hasAlert = !isInThePast && (classpassSoldOut || classpassAlmostSoldOut);
  const hasOpportunity =
    !isInThePast && displaySpotManagement && !isLocked && recommendation && !smartspotEnabled && !reservationsStopped;

  const groupClasses = withMods('reservations-overview__group');

  const classpassSpotsPickerMessage = isDesktop ? messages.cp.short : messages.cp.spots;
  const classpassBookingsMessage = isDesktop ? messages.cp.short : messages.cp.long;

  const status = hasAlert && (
    <div className={withMods('reservations-overview__status', { isDesktop })}>
      <Message message={classpassSoldOut ? messages.soldOut : messages.almostSoldOut} />
    </div>
  );

  return (
    <div className={withMods('reservations-overview')}>
      {showClasspassSpotsPicker && (
        <div className="reservations-overview__group-wrapper">
          <div className={withMods(groupClasses, 'reservations-overview__group--classpass-spots-picker')}>
            <div className={withMods('reservations-overview__fieldset', { hasOpportunity })}>
              <div className="reservations-overview__label">
                <Message message={classpassSpotsPickerMessage} />
              </div>
              <div className="reservations-overview__value reservations-overview__stepper-container">
                {status}
                <Form>
                  <NetworkReservationsStepper
                    currentClasspassSpots={currentClasspassSpots}
                    decrementClasspassSpots={decrementClasspassSpots}
                    incrementClasspassSpots={incrementClasspassSpots}
                    schedule={schedule}
                  />
                </Form>
                {hasOpportunity && !classpassDemand && (
                  <Button
                    className="reservations-overview__opportunity bt--sm bt--width-full"
                    styled="primary-outline"
                    onClick={actOnOpportunity}>
                    <Message message={messages.opportunityButton} values={{ recommendation }} />
                  </Button>
                )}
              </div>
              <div className="reservations-overview__value reservations-overview__dropdown-container">
                <span className="reservations-overview__value-text">{classpassBooked}</span>
                <span className="reservations-overview__value-text reservations-overview__value-text--muted">
                  <Message message={messages.of} />
                </span>
                <span className="reservations-overview__value-text">
                  <Form>
                    <Field
                      {...schedule}
                      name="classpassSpots"
                      parse={parseNumber}
                      component={NetworkReservationsDropdown}
                      props={{
                        warn: classpassAlmostSoldOut,
                        danger: classpassSoldOut,
                      }}
                    />
                  </Form>
                  {status}
                </span>
                {hasOpportunity && !classpassDemand && (
                  <Button
                    className="reservations-overview__opportunity bt--sm bt--text-sm"
                    styled="primary-outline"
                    onClick={actOnOpportunity}>
                    <Message message={messages.opportunityButton} values={{ recommendation }} />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="reservations-overview__group-wrapper">
        <div className={withMods(groupClasses, 'reservations-overview__group--multiple')}>
          <div className={withMods('reservations-overview__fieldset', { showClasspassSpotsPicker })}>
            <div className="reservations-overview__label">
              <Message message={classpassBookingsMessage} />
            </div>
            <Message
              as={!showBookingsPausedTooltip ? 'div' : TooltipToggle}
              className="reservations-overview__value"
              {...(!showBookingsPausedTooltip ? {} : { name: 'reservations-overview__tooltip-bookings-paused' })}
              message={messages.xofy}
              values={{
                x: classpassBooked,
                y: classpassTotal,
                span: text => <span className="reservations-overview__value-text">{text}</span>,
                muted: text => (
                  <span className="reservations-overview__value-text reservations-overview__value-text--muted">
                    {text}
                  </span>
                ),
              }}
            />
            {showBookingsPausedTooltip && (
              <TooltipContent name="reservations-overview__tooltip-bookings-paused">
                <Message message={messages.bookingsPaused} />
              </TooltipContent>
            )}
          </div>
        </div>
        {showSSAddSpotButton && !classpassDemand && (
          <Button
            className="reservations-overview__ss-add-spot-button bt--sm bt--text-sm"
            styled="primary-outline"
            onClick={handleSSAddSpot}>
            <Message message={messages.ss.ssAddSpotButton} />
          </Button>
        )}
      </div>
      {hasIntegrationData && (
        <div className="reservations-overview__group-wrapper">
          <div className={withMods(groupClasses, 'reservations-overview__group--direct-fill')}>
            <div className="reservations-overview__fieldset">
              <Message as="div" className="reservations-overview__label" message={messages.allBookings} />
              <Message
                as="div"
                className="reservations-overview__value"
                message={messages.xofy}
                values={{
                  x: totalBooked,
                  y: maxCapacity,
                  span: text => <span className="reservations-overview__value-text">{text}</span>,
                  muted: text => (
                    <span className="reservations-overview__value-text reservations-overview__value-text--muted">
                      {text}
                    </span>
                  ),
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

ReservationsOverview.propTypes = {
  schedule: PropTypes.shape(schedulePropTypes),
  displaySpotManagement: PropTypes.bool,
  handleSSAddSpot: PropTypes.func,
  isDesktop: PropTypes.bool,
  incrementClasspassSpots: PropTypes.func,
  decrementClasspassSpots: PropTypes.func,
  actOnOpportunity: PropTypes.func,
  currentValues: PropTypes.shape({
    classpassSpots: PropTypes.number,
  }),
  userId: PropTypes.number,
};

export default ReservationsOverview;
